import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Edit,
  Send,
  Upload,
  BarChart3,
  Award,
  FileText,
  GitBranch,
  ClipboardList,
  CheckCircle2,
  Layers,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import SourcingRfpService from '../../services/sourcingRFPService';

const LIFECYCLE_STAGES = [
  'Draft',
  'Generated',
  'Review',
  'Approved',
  'Published',
  'Responses',
  'Evaluated',
  'Awarded',
  'Contract',
];

const TABS = [
  { key: 'builder', label: 'Builder' },
  { key: 'preview', label: 'Preview' },
  { key: 'approval', label: 'Approval' },
  { key: 'responses', label: 'Responses' },
  { key: 'evaluation', label: 'Evaluation' },
  { key: 'award', label: 'Award' },
  { key: 'contract', label: 'Contract' },
];

const ACTIONS = {
  edit: { key: 'edit', label: 'Edit', icon: Edit },
  submit: { key: 'submit', label: 'Submit', icon: Send },
  publish: { key: 'publish', label: 'Publish', icon: Upload },
  evaluate: { key: 'evaluate', label: 'Evaluate', icon: BarChart3 },
  award: { key: 'award', label: 'Award', icon: Award },
  convert: { key: 'convert', label: 'Convert', icon: FileText },
};

const ALLOWED_ACTIONS_BY_STAGE = {
  Draft: ['edit', 'submit'],
  Generated: ['edit', 'submit'],
  Review: ['edit', 'submit'],
  Approved: ['publish'],
  Published: ['evaluate'],
  Responses: ['evaluate'],
  Evaluated: ['award'],
  Awarded: ['convert'],
  Contract: [],
};

const STATUS_STAGE_MAP = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  EVALUATION: 'Evaluated',
  AWARDED: 'Awarded',
  CANCELLED: 'Contract',
};

const initialState = {
  rfp: null,
  loading: false,
  activeTab: 'builder',
  currentStage: 'Draft',
  responses: [],
  responsesLoading: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_RFP':
      return {
        ...state,
        rfp: action.payload,
        currentStage: action.stage || state.currentStage,
      };
    case 'SET_TAB':
      return { ...state, activeTab: action.payload };
    case 'SET_STAGE':
      return { ...state, currentStage: action.payload };
    case 'SET_RESPONSES_LOADING':
      return { ...state, responsesLoading: action.payload };
    case 'SET_RESPONSES':
      return { ...state, responses: action.payload };
    default:
      return state;
  }
}

const normalizeStatus = (status) => String(status || '').toUpperCase();

const deriveStageFromRfp = (rfp) => {
  const lifecycleStage = rfp?.metadata?.lifecycleStage;
  if (lifecycleStage && LIFECYCLE_STAGES.includes(lifecycleStage)) {
    return lifecycleStage;
  }
  const normalized = normalizeStatus(rfp?.status);
  return STATUS_STAGE_MAP[normalized] || 'Draft';
};

const stageBadgeStyle = (stage) => {
  const map = {
    Draft: { bg: '#f8fafc', color: '#475569' },
    Generated: { bg: '#fff7ed', color: '#9a3412' },
    Review: { bg: '#fef3c7', color: '#92400e' },
    Approved: { bg: '#dcfce7', color: '#166534' },
    Published: { bg: '#dbeafe', color: '#1d4ed8' },
    Responses: { bg: '#e0f2fe', color: '#0369a1' },
    Evaluated: { bg: '#ede9fe', color: '#5b21b6' },
    Awarded: { bg: '#ccfbf1', color: '#115e59' },
    Contract: { bg: '#ecfccb', color: '#365314' },
  };
  return map[stage] || map.Draft;
};

const RfpProcessingMaster = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [state, dispatch] = useReducer(reducer, initialState);
  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const previewPayload = useMemo(() => {
    const htmlContent =
      state.rfp?.metadata?.htmlContent ||
      state.rfp?.metadata?.builderSections?.htmlContent ||
      '';
    const bindings = state.rfp?.metadata?.templateVariableMap || {};

    const withLoops = String(htmlContent || '').replace(
      /\{\{#each\s+([^}]+)\}\}([\s\S]*?)\{\{\/each\}\}/g,
      (_, arrayKey, inner) => {
        const arr = bindings[String(arrayKey).trim()];
        if (!Array.isArray(arr)) return '';
        return arr
          .map((item) =>
            String(inner)
              .replace(/\{\{\s*this\s*\}\}/g, String(item))
              .replace(/\{\{\s*label\s*\}\}/g, String(item?.label || ''))
              .replace(/\{\{\s*value\s*\}\}/g, String(item?.value || '')),
          )
          .join('');
      },
    );

    const rendered = withLoops.replace(
      /\{\{\s*([^#/][^}]*)\s*\}\}/g,
      (_, token) => {
        const key = String(token).trim();
        const value = bindings[key];
        return value === undefined || value === null ? '' : String(value);
      },
    );

    return { htmlContent, bindings, rendered };
  }, [state.rfp]);

  const previewValidation = useMemo(() => {
    const html = String(previewPayload.htmlContent || '').toLowerCase();
    const missing = [];
    if (!html.includes('introduction')) missing.push('Introduction section');
    if (!html.includes('project background'))
      missing.push('Project Background section');
    if (!html.includes('qualifying requirements'))
      missing.push('Qualifying Requirements section');
    if (!html.includes('financial bid')) missing.push('Financial Bid section');
    if (!html.includes('<table')) missing.push('Term-sheet table');
    return missing;
  }, [previewPayload]);

  const allowedActions = useMemo(
    () => ALLOWED_ACTIONS_BY_STAGE[state.currentStage] || [],
    [state.currentStage],
  );

  useEffect(() => {
    const loadRfp = async () => {
      if (!id) {
        dispatch({ type: 'SET_RFP', payload: null, stage: 'Draft' });
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const response = await SourcingRfpService.getRfpById(id);
        const rfpData = response?.data || null;
        const stage = deriveStageFromRfp(rfpData);
        dispatch({ type: 'SET_RFP', payload: rfpData, stage });
      } catch (err) {
        showErrorRef.current(err.message || 'Failed to load RFP details');
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    loadRfp();
  }, [id]);

  useEffect(() => {
    const loadResponses = async () => {
      if (state.activeTab !== 'responses' || !id) {
        return;
      }

      dispatch({ type: 'SET_RESPONSES_LOADING', payload: true });
      try {
        const response = await SourcingRfpService.getResponses(id);
        dispatch({ type: 'SET_RESPONSES', payload: response?.data || [] });
        if (state.currentStage === 'Published') {
          dispatch({ type: 'SET_STAGE', payload: 'Responses' });
        }
      } catch (err) {
        showErrorRef.current(err.message || 'Failed to load responses');
      } finally {
        dispatch({ type: 'SET_RESPONSES_LOADING', payload: false });
      }
    };

    loadResponses();
  }, [state.activeTab, id, state.currentStage]);

  const moveToStage = (nextStage) => {
    if (!LIFECYCLE_STAGES.includes(nextStage)) {
      return;
    }
    dispatch({ type: 'SET_STAGE', payload: nextStage });
  };

  const handleLifecycleAction = async (actionKey) => {
    if (!allowedActions.includes(actionKey)) {
      showError('Action not allowed for the current stage');
      return;
    }

    if (actionKey === 'edit') {
      dispatch({ type: 'SET_TAB', payload: 'builder' });
      navigate('/sourcing/rfps/create/manual', {
        state: {
          rfpId: id,
          sourceRfpId: id,
        },
      });
      return;
    }

    if (actionKey === 'submit') {
      if (state.currentStage === 'Draft') {
        moveToStage('Generated');
        success('Moved to Generated stage.');
      } else if (state.currentStage === 'Generated') {
        moveToStage('Review');
        success('Moved to Review stage.');
      } else if (state.currentStage === 'Review') {
        moveToStage('Approved');
        dispatch({ type: 'SET_TAB', payload: 'approval' });
        success('Moved to Approved stage.');
      }
      return;
    }

    if (actionKey === 'publish') {
      if (!id) {
        showError('RFP ID is required for publishing settings.');
        return;
      }
      navigate(`/sourcing/publish-vendor-mgmt/${id}?tab=publishing`);
      return;
    }

    if (actionKey === 'evaluate') {
      try {
        if (id) {
          await SourcingRfpService.startEvaluation(id);
        }
        moveToStage('Evaluated');
        dispatch({ type: 'SET_TAB', payload: 'evaluation' });
        success('RFP moved to Evaluated stage.');
      } catch (err) {
        showError(err.message || 'Failed to start evaluation');
      }
      return;
    }

    if (actionKey === 'award') {
      moveToStage('Awarded');
      dispatch({ type: 'SET_TAB', payload: 'award' });
      navigate(`/sourcing/evaluation-award/${id}?tab=award`);
      return;
    }

    if (actionKey === 'convert') {
      moveToStage('Contract');
      dispatch({ type: 'SET_TAB', payload: 'contract' });
      navigate('/sourcing/contracts/conversion', {
        state: { sourceRfpId: id, intent: 'convert-contract' },
      });
    }
  };

  const renderTabContent = () => {
    if (state.activeTab === 'builder') {
      return (
        <div>
          <p className="mb-2">Edit and manage section-wise builder content.</p>
          <button
            className="btn btn-outline-primary"
            onClick={() => handleLifecycleAction('edit')}
          >
            Open Builder
          </button>
        </div>
      );
    }

    if (state.activeTab === 'preview') {
      return (
        <div>
          {previewValidation.length > 0 && (
            <div
              className="mb-2"
              style={{ color: '#9a3412', fontSize: '13px' }}
            >
              Missing template markers: {previewValidation.join(', ')}
            </div>
          )}
          {!previewPayload.rendered ? (
            <p className="mb-0">No preview content available.</p>
          ) : (
            <div
              style={{
                border: '1px solid #e5e7eb',
                padding: '12px',
                background: '#fff',
              }}
              dangerouslySetInnerHTML={{ __html: previewPayload.rendered }}
            />
          )}
        </div>
      );
    }

    if (state.activeTab === 'approval') {
      return (
        <div>
          <p className="mb-2">
            Approval workflow status is controlled via lifecycle transitions.
          </p>
          <button
            className="btn btn-outline-primary"
            onClick={() => navigate('/sourcing/rfps/approval')}
          >
            Open Approval Queue
          </button>
        </div>
      );
    }

    if (state.activeTab === 'responses') {
      if (state.responsesLoading) {
        return <p className="mb-0">Loading responses...</p>;
      }

      return (
        <div>
          {state.responses.length === 0 ? (
            <p className="mb-0">No responses available yet.</p>
          ) : (
            <ul className="mb-0">
              {state.responses.map((response) => (
                <li key={response._id || response.id}>
                  {response.vendorName ||
                    response.vendor?.name ||
                    'Vendor submission'}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }

    if (state.activeTab === 'evaluation') {
      return (
        <p className="mb-0">
          Evaluation scoring and comparison controls live in this tab.
        </p>
      );
    }

    if (state.activeTab === 'award') {
      return (
        <p className="mb-0">
          Award decision and justification controls live in this tab.
        </p>
      );
    }

    if (state.activeTab === 'contract') {
      return (
        <p className="mb-0">
          Contract conversion and mapping controls live in this tab.
        </p>
      );
    }

    return null;
  };

  const stageStyle = stageBadgeStyle(state.currentStage);

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="RFP PROCESSING (MASTER)"
        description="Lifecycle control center with stage-based actions and transitions."
      />

      <div className="page-container">
        <div
          className="card mb-3"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '18px 20px' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <GitBranch size={16} />
                  <strong>RFP Details</strong>
                </div>
                <div
                  style={{ fontSize: '13px', color: 'var(--text-secondary)' }}
                >
                  <div>RFP ID: {state.rfp?.rfpNumber || id || 'N/A'}</div>
                  <div>Title: {state.rfp?.title || 'RFP Processing'}</div>
                  <div>
                    Project Type:{' '}
                    {state.rfp?.projectType || state.rfp?.category || 'General'}
                  </div>
                </div>
              </div>

              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <Layers size={16} />
                  <strong>Current Stage</strong>
                </div>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    borderRadius: '999px',
                    background: stageStyle.bg,
                    color: stageStyle.color,
                    fontWeight: 600,
                    fontSize: '12px',
                  }}
                >
                  {state.currentStage}
                </span>
              </div>
            </div>

            <hr />

            <div className="d-flex flex-wrap gap-2">
              {allowedActions.map((key) => {
                const cfg = ACTIONS[key];
                if (!cfg) return null;
                const Icon = cfg.icon;
                return (
                  <button
                    key={cfg.key}
                    className="btn btn-primary"
                    onClick={() => handleLifecycleAction(cfg.key)}
                    disabled={state.loading}
                  >
                    <Icon size={14} className="me-1" />
                    {cfg.label}
                  </button>
                );
              })}
              {allowedActions.length === 0 && (
                <span
                  style={{ fontSize: '13px', color: 'var(--text-secondary)' }}
                >
                  No actions available for this stage.
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className="card mb-3"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '12px 16px' }}>
            <div className="d-flex flex-wrap gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={`btn ${state.activeTab === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() =>
                    dispatch({ type: 'SET_TAB', payload: tab.key })
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          className="card"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '20px' }}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <ClipboardList size={16} />
              <h6 className="mb-0">
                {TABS.find((t) => t.key === state.activeTab)?.label}
              </h6>
              <CheckCircle2 size={14} color={stageStyle.color} />
            </div>
            {renderTabContent()}
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default RfpProcessingMaster;
